import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang, LANGS, WHATSAPP_URL } from "../i18n";
import { Logo, WhatsAppIcon } from "./Logo";

const LINKS = [
  { key: "how", href: "#how-it-works", id: "nav-how-it-works" },
  { key: "salons", href: "#hair-salons", id: "nav-hair-salons" },
  { key: "restaurants", href: "#restaurants", id: "nav-restaurants" },
  { key: "included", href: "#whats-included", id: "nav-whats-included" },
  { key: "pricing", href: "#pricing", id: "nav-pricing" },
  { key: "demo", href: "#ai-demo", id: "nav-ai-demo" },
  { key: "why", href: "#why-us", id: "nav-why-us" },
];

const LangSwitcher = ({ compact = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="lang-switcher"
      className="flex items-center gap-0.5 rounded-full border border-slate-700/60 bg-slate-900/60 p-0.5"
    >
      {LANGS.map((l) => (
        <button
          key={l.code}
          data-testid={`lang-switch-${l.code}`}
          onClick={() => setLang(l.code)}
          className={`rounded-full px-2 py-1 font-mono2 text-[10px] tracking-widest transition-colors ${
            lang === l.code
              ? "bg-emerald-500 text-slate-950 font-semibold"
              : "text-slate-400 hover:text-slate-100"
          } ${compact ? "" : "sm:px-2.5"}`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-nav"
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
        scrolled
          ? "border-b border-slate-800/70 bg-[#070B11]/85 backdrop-blur-xl py-2.5"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-10">
        <Logo compact={scrolled} />
        <nav className="hidden items-center gap-6 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={l.id}
              className="text-sm text-slate-400 transition-colors hover:text-emerald-300"
            >
              {t.nav[l.key]}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <LangSwitcher />
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="hidden items-center gap-2 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 px-4 py-2 text-sm font-semibold text-slate-950 shadow-[0_0_24px_rgba(16,185,129,0.35)] transition-transform hover:scale-105 md:inline-flex"
          >
            <WhatsAppIcon className="h-4 w-4" />
            {t.nav.cta}
          </a>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="rounded-full border border-slate-700/60 p-2 text-slate-300 lg:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-slate-800/70 bg-[#070B11]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.key}
                  href={l.href}
                  data-testid={`${l.id}-mobile`}
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-base text-slate-300 transition-colors hover:bg-slate-800/60 hover:text-emerald-300"
                >
                  {t.nav[l.key]}
                </a>
              ))}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-whatsapp-cta-mobile"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-4 py-3 text-sm font-semibold text-slate-950"
              >
                <WhatsAppIcon className="h-4 w-4" />
                {t.nav.cta}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
